// SelectRole.jsx - Role Selection Page for InfluenceHub MVP
// Matches onboarding wizard layout: AuthLayout, centered card, Step 0 of 2.
// Two modes: (1) Post-auth: save role to Firestore and redirect to onboarding. (2) Pre-signup: persist and navigate to signup/brand wizard.

import React, { useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig.js';
import { useAuth } from '../context/AuthContext.jsx';
import AuthLayout from '../components/auth/AuthLayout.jsx';

const SelectRole = ({ onNavigate }) => {
  const { user, profile, loading: authLoading } = useAuth();
  const [selectedRole, setSelectedRole] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (authLoading || !user || !profile) return;
    const r = profile.role ?? profile.userType;
    if (!r) return;
    if (r === 'brand') {
      const completed = profile.onboardingCompleted === true || profile.onboardingStatus === 'completed';
      onNavigate(completed ? '/brand/dashboard' : '/onboarding/brand', null, { replace: true });
    } else if (r === 'creator' || r === 'influencer') {
      const completed = profile.onboardingCompleted === true || profile.onboardingStatus === 'completed';
      onNavigate(completed ? '/profile/edit' : '/onboarding/step-1', null, { replace: true });
    }
  }, [user, profile, authLoading, onNavigate]);

  React.useEffect(() => {
    if (!authLoading && !user) {
      onNavigate('/login', null, { replace: true });
    }
  }, [authLoading, user, onNavigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-slate-600">Loading...</p>
      </div>
    );
  }
  if (!user) {
    return null;
  }

  const existingRole = profile?.role ?? profile?.userType;
  if (profile && (existingRole === 'creator' || existingRole === 'influencer' || existingRole === 'brand')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-slate-600">Redirecting...</p>
      </div>
    );
  }

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setError('');
  };

  const handleKeyDown = (event, role) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleRoleSelect(role);
    }
  };

  const handleContinue = async () => {
    if (!selectedRole) {
      setError('Please select a role to continue');
      return;
    }

    if (user) {
      setSaving(true);
      try {
        const uid = user.uid;
        const userRef = doc(db, 'users', uid);
        const roleValue = selectedRole === 'influencer' ? 'creator' : 'brand';
        const userTypeValue = selectedRole === 'influencer' ? 'influencer' : 'brand';

        await setDoc(
          userRef,
          {
            role: roleValue,
            userType: userTypeValue,
            onboardingCompleted: false,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );

        if (selectedRole === 'brand') {
          const brandRef = doc(db, 'brandProfiles', uid);
          const existing = await getDoc(brandRef);
          if (!existing.exists()) {
            await setDoc(brandRef, {
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            }, { merge: true });
          }
        }

        if (selectedRole === 'influencer') {
          // Create influencers doc so API (TikTok/Instagram refresh) can find it after Step 3
          const influencerRef = doc(db, 'influencers', uid);
          const existingInfluencer = await getDoc(influencerRef);
          if (!existingInfluencer.exists()) {
            await setDoc(influencerRef, {
              userId: uid,
              socialConsent: { publicData: true },
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            }, { merge: true });
          }
          onNavigate('/onboarding/step-1', null, { replace: true });
        } else {
          onNavigate('/onboarding/brand', null, { replace: true });
        }
      } catch (err) {
        console.error('[SelectRole] Failed to save role:', err);
        setError('Something went wrong. Please try again.');
        setSaving(false);
      }
      return;
    }

    localStorage.setItem('ih.selectedRole', selectedRole);
    if (selectedRole === 'influencer') {
      onNavigate('signup-wizard?fresh=1', null, { replace: false });
    } else if (selectedRole === 'brand') {
      onNavigate('brand-wizard?fresh=1', null, { replace: false });
    } else {
      onNavigate('signup', null, { replace: false });
    }
  };

  const cardBase = 'w-full rounded-lg border-2 p-4 cursor-pointer transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1B9DA2] focus:ring-offset-2';
  const cardInactive = 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50';
  const cardActive = 'border-[#1B9DA2] bg-[#1B9DA2]/5 ring-2 ring-[#1B9DA2]/20';

  return (
    <AuthLayout
      title="Welcome to InfluenceHub"
      subtitle="Select your account type to get started."
      cardClassName="px-6 py-6 sm:px-8"
    >
      <div className="mb-6">
        <p className="text-sm font-medium text-slate-600">Step 0 of 2</p>
        <div className="mt-2 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
          <div className="h-full bg-[#1B9DA2] transition-all duration-300" style={{ width: '0%' }} />
        </div>
      </div>

      <div className="space-y-3 mb-6">
        <button
          type="button"
          className={`flex items-center gap-3 text-left ${cardBase} ${selectedRole === 'influencer' ? cardActive : cardInactive}`}
          onClick={() => handleRoleSelect('influencer')}
          onKeyDown={(e) => handleKeyDown(e, 'influencer')}
          tabIndex={0}
          role="button"
          aria-pressed={selectedRole === 'influencer'}
          aria-label="Select Influencer role"
        >
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <span className="text-lg" aria-hidden>😊</span>
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-900">I am an Influencer</h3>
            <p className="text-sm text-slate-600 mt-0.5">Create content and collaborate with brands</p>
          </div>
        </button>

        <button
          type="button"
          className={`flex items-center gap-3 text-left ${cardBase} ${selectedRole === 'brand' ? cardActive : cardInactive}`}
          onClick={() => handleRoleSelect('brand')}
          onKeyDown={(e) => handleKeyDown(e, 'brand')}
          tabIndex={0}
          role="button"
          aria-pressed={selectedRole === 'brand'}
          aria-label="Select Brand role"
        >
          <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
            <span className="text-lg" aria-hidden>🏢</span>
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-900">I am a Brand</h3>
            <p className="text-sm text-slate-600 mt-0.5">Find creators and launch campaigns</p>
          </div>
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={handleContinue}
        disabled={!selectedRole || saving}
        className="h-12 w-full rounded-lg bg-[#1B9DA2] text-sm font-medium text-white hover:bg-[#168589] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#1B9DA2] focus:ring-offset-2"
      >
        {saving ? 'Saving...' : 'Continue'}
      </button>
    </AuthLayout>
  );
};

export default SelectRole;
